import { SagaIterator } from 'redux-saga';
import { call, take, takeLatest } from 'redux-saga/effects';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { App } from '@ps20-frontend/frontend-models';
import { PS20Logger } from '@ps20-frontend/logging';
import {
  AngebotsId,
  GlobalAction,
  GlobalActions,
  GlobalPut,
  WrappedSavedStatesLoaded,
} from '@ps20-frontend/shop-integration-redux';
import { collectOldEleProducts } from './cleanupOldEleProductsSaga';
import { collectOldNbgProducts } from './cleanupOldNbgProductsSaga';
import { collectOldVhvExtremwetterProducts } from './cleanupOldVhvExtremwetterProductsSaga';
import { collectOrphanedTiergruppeProducts } from './cleanupOrphanedTiergruppeProductsSaga';

export function* watchCleanupLoadedProducts() {
  yield takeLatest(
    GlobalActions.SAVED_STATES_LOADED,
    safeSaga(cleanupAfterLoadedStatesUpdate, App.SHOP_DASHBOARD),
  );
}

function* cleanupAfterLoadedStatesUpdate(
  action: GlobalAction<WrappedSavedStatesLoaded>,
): SagaIterator {
  yield take(GlobalActions.UPDATE_LOADED_STATES);
  yield* cleanupLoadedProducts(action);
}

function* collectIsolated(
  cleanupName: string,
  collect: () => AngebotsId[] | SagaIterator<AngebotsId[]>,
): SagaIterator<AngebotsId[]> {
  try {
    const collected = collect();
    return Array.isArray(collected) ? collected : yield* collected;
  } catch (error) {
    PS20Logger.error(App.SHOP_DASHBOARD, error as Error, `Load cleanup ${cleanupName} failed`);
    return [];
  }
}

export function* cleanupLoadedProducts(
  action: GlobalAction<WrappedSavedStatesLoaded>,
): SagaIterator {
  const eleProducts: AngebotsId[] = yield* collectIsolated('ELE', () =>
    collectOldEleProducts(action),
  );
  const nbgProducts: AngebotsId[] = yield* collectIsolated('NBG', () =>
    collectOldNbgProducts(action),
  );
  const vhvProducts: AngebotsId[] = yield* collectIsolated('VHV', () =>
    collectOldVhvExtremwetterProducts(action),
  );
  const tiergruppeProducts: AngebotsId[] = yield* collectIsolated('TGV', () =>
    collectOrphanedTiergruppeProducts(action),
  );

  const uniqueProducts = new Map<string, AngebotsId>();
  [...eleProducts, ...nbgProducts, ...vhvProducts, ...tiergruppeProducts].forEach(produkt =>
    uniqueProducts.set(`${produkt.appId}|${produkt.angebotsId}`, produkt),
  );

  if (uniqueProducts.size === 0) {
    return;
  }

  const globalPut = GlobalPut.getInstance();
  yield call(
    globalPut.put.bind(globalPut),
    new GlobalActions().produktDeleted(Array.from(uniqueProducts.values())),
  );
}
